import React, { useState, useEffect, useMemo, useRef } from "react";
import { Breadcrumb } from "../../components/Breadcrumb";
import { FORM_CONSTANTS } from "./constants";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { useLocation, useParams } from "react-router-dom";
import { FormData } from "./types";
import { useJsonTemplates, useBonusDetails } from "../../hooks/useBonusQueries";
import { getPreviousQuarter } from "../../utils/dateUtils";
import { ActionButtons } from "../../utils/renderActionButtons";
import CommentComponent from "../../components/SubForms/Common/CommentComponent";
import BonusApplicationHistory from "../../components/SubForms/Common/BonusApplicationHistory";
import { useComments, useHistory } from "../../hooks/useFormHelpers";
import { getBonusSheetJsonData, parseBonusSheetJsonData } from "../../utils/bonusSheetParser";

const BONUS_TIERS = [
  { threshold: 70, bonus: 100 }, { threshold: 66, bonus: 95 }, { threshold: 63, bonus: 90 },
  { threshold: 56, bonus: 80 }, { threshold: 50, bonus: 50 }, { threshold: 0, bonus: 0 },
] as const;

const calculateBonusPercent = (percent: number): number =>
  BONUS_TIERS.find(tier => percent >= tier.threshold)?.bonus ?? 0;

const setByPath = <T,>(obj: T, path: string[], value: any): T => {
  const [head, ...rest] = path;
  if (!head) return obj;
  return { ...(obj as any), [head]: rest.length ? setByPath((obj as any)[head] ?? {}, rest, value) : value } as T;
};

// ── Shared input style helpers ────────────────────────────────────────────────
const inputBase: React.CSSProperties = { width: "100%", padding: "8px 12px", fontSize: 13, borderRadius: 7, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
const inputEditable: React.CSSProperties = { ...inputBase, background: "var(--bg-input)", color: "var(--text-primary)", border: "1px solid var(--border-default)" };
const inputRo: React.CSSProperties = { ...inputBase, background: "var(--bg-input-readonly)", color: "var(--text-readonly)", border: "1px solid var(--border-readonly)", cursor: "not-allowed", pointerEvents: "none" };
const labelStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 500, color: "var(--text-label)", marginBottom: 4 };
const sectionAccent = (
  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
);

const CCMForm: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const selectedRole = localStorage.getItem("selectedRole") || "";
  const { id } = useParams<{ id: string }>();
  const { data: templates, isLoading } = useJsonTemplates(selectedRole);
  const { data: bonusDetails, isLoading: isLoadingDetails } = useBonusDetails(parseInt(id || "0"));
  const totalCallsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [bonusSheetData, setBonusSheetData] = useState<any>(null);

  const queryParams = new URLSearchParams(location.search);
  const isReviewMode = queryParams.get("mode") === "review";
  const editableStatuses = [null, "Draft", "Rejected"];
  const isEditable = editableStatuses.includes(bonusDetails?.currentStatus ?? null);
  
  const initQueryParam = queryParams.get("init") || null;
  const prevQuarterYear = getPreviousQuarter();

  // const [hasFoundationCalls, setHasFoundationCalls] = useState<boolean>(false);

  const [bonusSheetTitle, setBonusSheetTitle] = useState<string>(`${selectedRole} Quarterly Bonus`);
  const [applicantInfo, setApplicantInfo] = useState({ name: localStorage.getItem("loggedInUserFullName") ?? "", email: localStorage.getItem("loggedInUserEmail") ?? "" });

  const [loggedInUser, setLoggedInUser] = useState({ name: localStorage.getItem("loggedInUserFullName") ?? "", email: localStorage.getItem("loggedInUserEmail") ?? "" });
  
  const { comment, commentList, commentsAccordionExpanded, handleCommentChange, handleAddComment, handleCommentsAccordionToggle } = useComments(bonusDetails, selectedRole);
  const { historyItems, historyAccordionExpanded, handleHistoryAccordionToggle } = useHistory(bonusDetails);

  const formatCurrency = (value: string | number): string => {
    if (!value || value === "0" || isNaN(Number(value))) return "";
    return parseFloat(value.toString()).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const [formData, setFormData] = useState<FormData>({
    managerName: "",
    managerEmail: "",

    applicationType: "",
    bonusRecipientName: "",
    bonusRecipientEmail: "",
    submitterName: (localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "",
    submitterEmail: (localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "",
    vpName: "",
    vpEmail: "",
    opsName: FORM_CONSTANTS.OPS.NAME,
    opsEmail: FORM_CONSTANTS.OPS.EMAIL,
    communityNumber: "", 
    year: prevQuarterYear.year.toString(), 
    quarter: prevQuarterYear.quarter.toString(), 
    region: "Other",
    hasFoundationCalls: false,
    hasOverriddenHierarchy: false,
    jobTitle: "",
    jobFamily: "",
    customerExperience: {
      customerCareCalls: {
        under7Completed: "", totalCallsCompleted: "", percentCompletedUnder7: "", bonusPercentEarned: "",
        foundationCalls: "0", flatBonusRatePerFoundationCall: "125.00",
        extensiveFoundationCalls: "0", flatBonusRatePerExtensiveFoundationCall: "250.00", additionalBonus: "0.00",
      },
    },
    bonusCalculation: {
      totalAchievementScore: { flatBonusRatePerCompletedCall: "30.00", callsCompleted: "", bonusPoolAvailableAt100Percent: "" },
      bonusPool: "", percentage: "", totalBonusPayout: "",
    },
    signatures: { divisionManagerSignature: "", divisionManagerDate: "", vpSignature: "", vpDate: "" },
  });

  const initQueryData = useMemo(() => {
    if (!initQueryParam) return null;
    try {
      return JSON.parse(atob(decodeURIComponent(initQueryParam)));
    } catch (e) {
      console.error("Failed to parse ?init= param", e);
      return null;
    }
  }, [initQueryParam]);


  // ── All useEffects preserved exactly ─────────────────────────────────────
  useEffect(() => {
    if (!initQueryData) return;
    try {
      // const init = JSON.parse(atob(decodeURIComponent(initQueryParam)));
      setBonusSheetTitle(`${initQueryData?.applicationType} Quarterly Bonus`);
      // setHasFoundationCalls(init.hasFoundationCalls || false);
      setFormData(prev => ({ ...prev, 
          hasFoundationCalls: initQueryData?.hasFoundationCalls || false,
          hasOverriddenHierarchy: initQueryData?.hasOverriddenHierarchy || false,
          jobTitle: initQueryData?.jobTitle || prev.jobTitle,
          jobFamily: initQueryData?.jobFamily || prev.jobFamily,

          managerName: initQueryData?.bonusRecipientName || prev.managerName, 
          managerEmail: initQueryData?.bonusRecipientEmail || prev.managerEmail,

          applicationType: initQueryData?.applicationType || prev.applicationType,
          bonusRecipientName: initQueryData?.bonusRecipientName || prev.bonusRecipientName,
          bonusRecipientEmail: initQueryData?.bonusRecipientEmail || prev.bonusRecipientEmail,
          submitterName: initQueryData?.submitterName || prev.submitterName,
          submitterEmail: initQueryData?.submitterEmail || prev.submitterEmail,
          vpName: initQueryData?.vpName || prev.vpName,
          vpEmail: initQueryData?.vpEmail || prev.vpEmail, 
          year: initQueryData?.year?.toString() || prev.year, 
          quarter: initQueryData?.quarter?.toString() || prev.quarter, 
          signatures: { ...prev.signatures, vpSignature: initQueryData?.vpName || prev.signatures.vpSignature } }));
      setApplicantInfo({ name: initQueryData?.bonusRecipientName || "", email: initQueryData?.bonusRecipientEmail || "" });
    } catch (e) { console.error("Failed to parse ?init= param", e); }
  }, [initQueryData]);

  useEffect(() => {
    if (bonusDetails && id) {
      try {
        const rawBonusSheet = getBonusSheetJsonData(bonusDetails);
        if (!rawBonusSheet) return;
        const bsd = parseBonusSheetJsonData(rawBonusSheet) as any;
        setBonusSheetData(bsd);
        console.log("Parsed bonus sheet data:", bsd);
        setBonusSheetTitle(`${bonusDetails.applicationType} Quarterly Bonus`);
        
        // setHasFoundationCalls((bonusDetails?.bonusSheet as any)?.hasFoundationCalls || false);
        console.log("Setting form data with bonus sheet details:", { applicationType: bonusDetails.applicationType, managerName: bonusDetails?.employeeName, managerEmail: bonusDetails?.createdBy, year: bonusDetails.year, quarter: bonusDetails.quarter, customerExperience: bsd?.customerExperience, bonusCalculation: bsd?.bonusCalculation });

        setFormData(prev => {
          const recipientNameLoaded = bsd?.bonusRecipientName || bsd?.managerName || bsd?.applicantName || bonusDetails?.employeeName || prev.bonusRecipientName;
          const recipientEmailLoaded = bsd?.bonusRecipientEmail || bsd?.managerEmail || bsd?.applicantEmail || bonusDetails?.bonusRecipientEmail || prev.bonusRecipientEmail;
          return {
            ...prev,
            applicationType: bonusDetails.applicationType || prev.applicationType,
            managerName: recipientNameLoaded,
            managerEmail: recipientEmailLoaded,
            bonusRecipientName: recipientNameLoaded,
            bonusRecipientEmail: recipientEmailLoaded,
            submitterName: bonusDetails?.submittedByName || prev.submitterName,
            submitterEmail: bonusDetails?.submittedBy || prev.submitterEmail,
            vpName: bsd?.vpName || prev.vpName,
            vpEmail: bsd?.vpEmail || prev.vpEmail,
            opsName: bsd?.opsName || prev.opsName,
            opsEmail: bsd?.opsEmail || prev.opsEmail,
            year: bonusDetails.year.toString() || prev.year,
            hasFoundationCalls: bonusDetails?.bonusSheet?.hasFoundationCalls || false,
            jobTitle: bsd?.jobTitle || prev.jobTitle,
            jobFamily: bsd?.jobFamily || prev.jobFamily,
            hasOverriddenHierarchy: bonusDetails?.hasOverriddenHierarchy || false,
            quarter: bonusDetails.quarter.toString() || prev.quarter, customerExperience: bsd?.customerExperience || prev.customerExperience, bonusCalculation: bsd?.bonusCalculation || prev.bonusCalculation
          };
        });

        if (bonusDetails.createdBy) setApplicantInfo(prev => ({ ...prev, email: bonusDetails.createdBy }));
      } catch (error) { console.error("Error parsing bonus sheet data:", error); }
    }
  }, [bonusDetails, id]);

  const derivedCalculations = useMemo(() => {
    const { customerCareCalls } = formData.customerExperience;
    const hasFoundationCalls = formData.hasFoundationCalls;
    const u7 = parseFloat(customerCareCalls.under7Completed || "0");
    const tot = parseFloat(customerCareCalls.totalCallsCompleted || "0");
    const pctU7 = tot ? (u7 / tot) * 100 : 0;
    const fc = hasFoundationCalls ? parseFloat(customerCareCalls.foundationCalls || "0") : 0;
    const fcRate = hasFoundationCalls ? parseFloat(customerCareCalls.flatBonusRatePerFoundationCall || "125") : 0;
    const efc = hasFoundationCalls ? parseFloat(customerCareCalls.extensiveFoundationCalls || "0") : 0;
    const efcRate = hasFoundationCalls ? parseFloat(customerCareCalls.flatBonusRatePerExtensiveFoundationCall || "250") : 0;
    const addBonus = hasFoundationCalls ? parseFloat(customerCareCalls.additionalBonus || "0") : 0;
    const flatRate = parseFloat(formData.bonusCalculation.totalAchievementScore.flatBonusRatePerCompletedCall ?? "0");
    const bonusPool = tot * flatRate || 0;
    const bonusPct = calculateBonusPercent(pctU7);
    const pct = (bonusPct / 100) || 0;
    const totalPayout = (bonusPool * pct) + (fc * fcRate) + (efc * efcRate) + addBonus;
    return {
      percentCompletedUnder7: pctU7.toFixed(2), bonusPercentEarned: bonusPct.toFixed(2),
      bonusPoolAvailableAt100Percent: bonusPool.toFixed(2), bonusPool: bonusPool.toFixed(2),
      percentage: pct.toFixed(2), totalBonusPayout: totalPayout.toFixed(2),
    };
  }, [formData.customerExperience.customerCareCalls.under7Completed, formData.customerExperience.customerCareCalls.totalCallsCompleted, formData.customerExperience.customerCareCalls.foundationCalls, formData.customerExperience.customerCareCalls.extensiveFoundationCalls, formData.customerExperience.customerCareCalls.additionalBonus, formData.customerExperience.customerCareCalls.flatBonusRatePerFoundationCall, formData.customerExperience.customerCareCalls.flatBonusRatePerExtensiveFoundationCall, formData.bonusCalculation.totalAchievementScore.flatBonusRatePerCompletedCall, formData.region, formData.hasFoundationCalls]);

  useEffect(() => {
    setFormData(prev => {
      if (prev.customerExperience.customerCareCalls.percentCompletedUnder7 === derivedCalculations.percentCompletedUnder7 && prev.customerExperience.customerCareCalls.bonusPercentEarned === derivedCalculations.bonusPercentEarned && prev.bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100Percent === derivedCalculations.bonusPoolAvailableAt100Percent && prev.bonusCalculation.bonusPool === derivedCalculations.bonusPool && prev.bonusCalculation.percentage === derivedCalculations.percentage && prev.bonusCalculation.totalBonusPayout === derivedCalculations.totalBonusPayout) return prev;
      return { ...prev, customerExperience: { ...prev.customerExperience, customerCareCalls: { ...prev.customerExperience.customerCareCalls, percentCompletedUnder7: derivedCalculations.percentCompletedUnder7, bonusPercentEarned: derivedCalculations.bonusPercentEarned } }, bonusCalculation: { ...prev.bonusCalculation, totalAchievementScore: { ...prev.bonusCalculation.totalAchievementScore, bonusPoolAvailableAt100Percent: derivedCalculations.bonusPoolAvailableAt100Percent }, bonusPool: derivedCalculations.bonusPool, percentage: derivedCalculations.percentage, totalBonusPayout: derivedCalculations.totalBonusPayout } };
    });
  }, [derivedCalculations]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, options?: { type?: 'number' | 'decimal' | 'select' | 'text'; maxLength?: number; minLength?: number }) => {
    let { name, value } = e.target;
    if (options) { if (options.maxLength !== undefined && value.length > options.maxLength) return; if (options.minLength !== undefined && value !== '' && value.length < options.minLength) return; }
    value = typeof value === "string" ? value.replace(/,/g, "").trim() : "";
    const type = options?.type || "text";
    if (type === "number") { if (!(value === '' || /^[0-9]*$/.test(value))) return; }
    else if (type === "decimal") { if (!(value === '' || /^\d*\.?\d{0,2}$/.test(value))) return; }
    if (name === "customerExperience.customerCareCalls.under7Completed") { const tc = parseInt(formData.customerExperience.customerCareCalls.totalCallsCompleted || "0"); const u7v = parseInt(value || "0"); if (formData.customerExperience.customerCareCalls.totalCallsCompleted && tc > 0 && u7v > tc) value = tc.toString(); }
    if (name === "customerExperience.customerCareCalls.totalCallsCompleted") {
      if (totalCallsDebounceRef.current) clearTimeout(totalCallsDebounceRef.current);
      setFormData(prev => setByPath(prev, name.split("."), value));
      const u7 = parseInt(formData.customerExperience.customerCareCalls.under7Completed || "0");
      const tcv = parseInt(value || "0");
      if (formData.customerExperience.customerCareCalls.under7Completed && u7 > 0 && value !== '' && tcv < u7)
        totalCallsDebounceRef.current = setTimeout(() => setFormData(prev => setByPath(prev, ["customerExperience","customerCareCalls","under7Completed"], value)), 500);
      return;
    }
    if (type === "select" && e.target.name === "region") {
      setFormData(prev => value === "Other"
        ? { ...prev, region: value, customerExperience: { ...prev.customerExperience, customerCareCalls: { ...prev.customerExperience.customerCareCalls, foundationCalls: "0", flatBonusRatePerFoundationCall: "125.00", extensiveFoundationCalls: "0", flatBonusRatePerExtensiveFoundationCall: "250.00", additionalBonus: "0.00" } } }
        : { ...prev, region: value });
      return;
    }
    setFormData(prev => setByPath(prev, name.split("."), value));
  };

  const validateForm = () => {
    const errors: string[] = [];

    const isEditableReviewState =
      isReviewMode &&
      (!bonusDetails?.currentStatus ||
        ["Draft", "Rejected"].includes(bonusDetails.currentStatus));

    if (!isEditableReviewState) {
      return errors;
    }

    const { customerCareCalls } = formData.customerExperience;
    const { hasFoundationCalls } = formData;

    const toInt = (value?: string) => parseInt(value || "0", 10);
    const toFloat = (value?: string) => parseFloat(value || "0");

    const values = {
      under7Calls: toInt(customerCareCalls.under7Completed),
      totalCalls: toInt(customerCareCalls.totalCallsCompleted),
      foundationCalls: toInt(customerCareCalls.foundationCalls),
      extensiveFoundationCalls: toInt(customerCareCalls.extensiveFoundationCalls),
      additionalBonus: toFloat(customerCareCalls.additionalBonus),
    };

    const hasValidStandardCalls =
      values.under7Calls > 0 && values.totalCalls > 0;

    const hasValidFoundationOrBonus =
      values.foundationCalls > 0 ||
      values.extensiveFoundationCalls > 0 ||
      values.additionalBonus > 0;

    const validationPassed = hasFoundationCalls
      ? hasValidStandardCalls || hasValidFoundationOrBonus
      : hasValidStandardCalls;

    if (!validationPassed) {
      errors.push(
        hasFoundationCalls
          ? "Either 'Under 7 Completed' AND 'Total Calls Completed' must be greater than 0, OR provide Foundation/Extensive Foundation Calls or Additional Bonus greater than 0."
          : "Both 'Under 7 Completed' AND 'Total Calls Completed' must be greater than 0."
      );
    }

    return errors;
  };

  
  const hasOverriddenHierarchy = initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy;
  const recipientEmail = initQueryData?.bonusRecipientDetails?.Email || bonusDetails?.bonusRecipientEmail;
  const recipientName = initQueryData?.bonusRecipientDetails?.EmployeeName || bonusDetails?.employeeName;
  const recipientJobTitle = initQueryData?.bonusRecipientDetails?.JobTitle || bonusSheetData?.jobTitle || formData.jobTitle || "";
  const recipientJobFamily = initQueryData?.bonusRecipientDetails?.JobFamily || bonusSheetData?.jobFamily || formData.jobFamily || "";
  const applicationType = initQueryData?.applicationType || bonusDetails?.applicationType;

  const buildMetadata = () => {
    const recipientNameFinal = recipientName || formData.bonusRecipientName || bonusDetails?.employeeName || "";
    const recipientEmailFinal = recipientEmail || formData.bonusRecipientEmail || "";
    return {
      applicationType: applicationType || formData.applicationType || "",
      bonusRecipientName: recipientNameFinal,
      bonusRecipientEmail: recipientEmailFinal,
      managerName: recipientNameFinal,
      managerEmail: recipientEmailFinal,
      applicantName: recipientNameFinal,
      applicantEmail: recipientEmailFinal,
      submitterName: initQueryData?.submitterName || bonusDetails?.submittedByName || formData.submitterName || "",
      submitterEmail: initQueryData?.submitterEmail || bonusDetails?.submittedBy || formData.submitterEmail || "",
      vpName: formData.vpName || "",
      vpEmail: formData.vpEmail || "",
      opsName: formData.opsName || "",
      opsEmail: formData.opsEmail || "",
      hasOverriddenHierarchy: formData.hasOverriddenHierarchy || false,
      jobTitle: recipientJobTitle || formData?.jobTitle || "",
      jobFamily: recipientJobFamily || formData?.jobFamily || "",
    };
  };

  const getBonusSheetJson = () => btoa(JSON.stringify({
    region: formData.region,
    hasFoundationCalls: formData.hasFoundationCalls,
    customerExperience: { customerCareCalls: { under7Completed: formData.customerExperience.customerCareCalls.under7Completed, totalCallsCompleted: formData.customerExperience.customerCareCalls.totalCallsCompleted, percentCompletedUnder7: formData.customerExperience.customerCareCalls.percentCompletedUnder7, bonusPercentEarned: formData.customerExperience.customerCareCalls.bonusPercentEarned, foundationCalls: formData.customerExperience.customerCareCalls.foundationCalls, flatBonusRatePerFoundationCall: formData.customerExperience.customerCareCalls.flatBonusRatePerFoundationCall, extensiveFoundationCalls: formData.customerExperience.customerCareCalls.extensiveFoundationCalls, flatBonusRatePerExtensiveFoundationCall: formData.customerExperience.customerCareCalls.flatBonusRatePerExtensiveFoundationCall, additionalBonus: formData.customerExperience.customerCareCalls.additionalBonus } },
    bonusCalculation: { totalAchievementScore: { flatBonusRatePerCompletedCall: formData.bonusCalculation.totalAchievementScore.flatBonusRatePerCompletedCall, callsCompleted: formData.bonusCalculation.totalAchievementScore.callsCompleted, bonusPoolAvailableAt100Percent: formData.bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100Percent }, bonusPool: formData.bonusCalculation.bonusPool, percentage: formData.bonusCalculation.percentage, totalBonusPayout: formData.bonusCalculation.totalBonusPayout },
    ...buildMetadata(),
  }));

  const breadcrumbPages = [{ name: FORM_CONSTANTS.BREADCRUMB.TITLE, href: FORM_CONSTANTS.BREADCRUMB.PATH, current: true, icon: (props: React.ComponentProps<"svg">) => <ClipboardDocumentCheckIcon {...props} className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900" /> }];

  if (isLoading || isLoadingDetails) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-page)" }}>
      <div style={{ color: "var(--text-muted)" }}>Loading…</div>
    </div>
  );

  const status = bonusDetails?.currentStatus?.replaceAll("_", " ") ?? "Draft";
  const statusColors: Record<string, React.CSSProperties> = {
    Draft:     { background: "#FEF9C3", color: "#854D0E", border: "1px solid #FDE047" },
    Submitted: { background: "#DBEAFE", color: "#1E40AF", border: "1px solid #93C5FD" },
    Approved:  { background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" },
    Rejected:  { background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" },
    Paid:      { background: "#D1FAE5", color: "#065F46", border: "1px solid #6EE7B7" },
  };
  const statusStyle = statusColors[status] ?? statusColors["Draft"];
  const fmtNum = (v: string) => v && v !== "0" && !isNaN(Number(v)) ? formatCurrency(v) : "";



  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-page)" }}>
      <div className="flex flex-col">
        <main className="flex flex-col pb-8">
          <div className={`mx-auto w-full pt-6 ${FORM_CONSTANTS.LAYOUT.MAX_WIDTH} ${FORM_CONSTANTS.LAYOUT.PADDING}`}>
            <Breadcrumb pages={breadcrumbPages} className="mb-6 ml-2" />

            {/* Error banner */}
            {error && (
              <div style={{ marginBottom: 16, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", padding: "12px 16px", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <span style={{ fontSize: 13 }}><strong>Error: </strong>{error}</span>
                <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#991B1B", fontSize: 18, lineHeight: 1, padding: "0 0 0 12px" }}>×</button>
              </div>
            )}

            <div style={{ background: "var(--bg-surface)", borderRadius: 12, border: "1px solid var(--border-default)", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>

              {/* ── Card header ─────────────────────────────────────────── */}
              <div style={{ borderBottom: "1px solid var(--border-section)", padding: "20px 24px", background: "var(--bg-form-header)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>{bonusSheetTitle}</h1>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, ...statusStyle }}>{status}</span>
                </div>
                <p style={{ margin: "0 0 16px", fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", lineHeight: 1.5 }}>
                  Achievement of Targets is a subjective determination in the Employer's sole discretion. The Employer may change the percentages or Targets at any time.
                </p>

                {/* Responsive field grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                  {/* Bonus Recipient */}
                  <div style={{ gridColumn: "span 2" }}>
                    <label style={labelStyle}>Bonus Recipient</label>
                    <input type="text" name="managerName" value={formData.managerName} readOnly style={{ ...inputRo }} />
                  </div>
                  {/* Year */}
                  <div>
                    <label style={labelStyle}>Year</label>
                    <input type="text" name="year" value={formData.year} readOnly style={{ ...inputRo }} />
                  </div>
                  {/* Quarter */}
                  <div>
                    <label style={labelStyle}>Quarter</label>
                    <input type="text" name="quarter" value={formData.quarter} readOnly style={{ ...inputRo }} />
                  </div>
                  {/* Region */}
                  {/* <div>
                    <label style={labelStyle}>Region</label>
                    <div style={{ position: "relative" }}>
                      <select name="region" value={formData.region || "Other"}
                        onChange={e => handleInputChange(e as React.ChangeEvent<HTMLSelectElement>, { type: "select" })}
                        disabled={!isEditable}
                        style={{ ...inputEditable, appearance: "none", paddingRight: 28, cursor: isEditable ? "pointer" : "not-allowed", opacity: isEditable ? 1 : 0.7 }}>
                        <option value="">Select Region</option>
                        <option value="DFW">DFW</option>
                        <option value="Other">Other</option>
                      </select>
                      <div style={{ pointerEvents: "none", position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "5px solid var(--text-muted)" }} />
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Override active banner */}
             {hasOverriddenHierarchy && (
              <div style={{ background: "#fff5f5", border: "1.5px solid #f87171", borderRadius: "8px", padding: "12px 16px", fontSize: 12, color: "#991b1b", display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Override Active: Employee Hierarchy is Overridden</span>
                </div>
                <span style={{ color: "#7f1d1d", lineHeight: 1.5 }}>
                  {status === "Draft"
                    ? "This recipient was selected with an active hierarchy override — all Construction & Customer Care employees were available for selection, not just direct reports."
                    : "This recipient was selected under a hierarchy override — the standard reporting hierarchy was not enforced at the time of selection."}
                </span>
                {(recipientName || recipientJobTitle || recipientJobFamily || applicationType) && (
                  <div style={{ marginTop: 4, padding: "8px 10px", background: "#fee2e2", borderRadius: "6px", display: "flex", flexWrap: "wrap", gap: "4px 20px", color: "#7f1d1d" }}>
                    {recipientName && <span><span style={{ fontWeight: 600 }}>Recipient:</span> {recipientName}</span>}
                    {recipientJobTitle && <span><span style={{ fontWeight: 600 }}>Recipient Job Title:</span> {recipientJobTitle}</span>}
                    {recipientJobFamily && <span><span style={{ fontWeight: 600 }}>Job Family:</span> {recipientJobFamily}</span>}
                    {applicationType && <span><span style={{ fontWeight: 600 }}>Application Type:</span> {applicationType}</span>}
                  </div>
                )}
              </div>
            )}

              {/* ── CUSTOMER EXPERIENCE ────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
                  {sectionAccent}
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Customer Experience</span>
                </div>

                <div className="sf-section">
                  <h3 className="sf-title">Customer Care Calls (70% of total achievement score)</h3>
                  <p className="sf-subtitle">This will be evaluated from calls completed within 7 days. The goal is 70% of all calls completed within 7 days.</p>

                  {/* Responsive 2-col on md+, 1-col on mobile */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }}>

                    {/* Left column — always visible */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      <div>
                        <label className="sf-label">Under 7 Completed</label>
                        <input type="number" name="customerExperience.customerCareCalls.under7Completed"
                          value={formData.customerExperience.customerCareCalls.under7Completed}
                          inputMode="numeric" maxLength={3} minLength={1}
                          disabled={!isEditable} placeholder="0" onFocus={e => e.target.select()}
                          onChange={e => handleInputChange(e, { type: "number", maxLength: 3, minLength: 1 })}
                          className="sf-input" style={{ padding: "8px 12px", textAlign: "right" }} />
                      </div>
                      <div>
                        <label className="sf-label">Total Calls Completed</label>
                        <input type="number" name="customerExperience.customerCareCalls.totalCallsCompleted"
                          value={formData.customerExperience.customerCareCalls.totalCallsCompleted}
                          inputMode="numeric" maxLength={3} minLength={1}
                          disabled={!isEditable} placeholder="0" onFocus={e => e.target.select()}
                          onChange={e => handleInputChange(e, { type: "number", maxLength: 3, minLength: 1 })}
                          className="sf-input" style={{ padding: "8px 12px", textAlign: "right" }} />
                      </div>
                      <div>
                        <label className="sf-label">% Completed Under 7</label>
                        <div style={{ position: "relative" }}>
                          <input type="number" readOnly name="customerExperience.customerCareCalls.percentCompletedUnder7"
                            value={formData.customerExperience.customerCareCalls.percentCompletedUnder7 || ""}
                            className="sf-input-ro" style={{ padding: "8px 32px 8px 12px", textAlign: "right", width: "100%" }} />
                          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-muted)" }}>%</span>
                        </div>
                      </div>
                    </div>

                    {/* Right column — DFW only */}
                    {formData.hasFoundationCalls && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        <div>
                          <label className="sf-label">Standard Foundation Calls</label>
                          <input type="number" name="customerExperience.customerCareCalls.foundationCalls"
                            value={formData.customerExperience.customerCareCalls.foundationCalls}
                            inputMode="numeric" maxLength={3} minLength={1}
                            disabled={!isEditable} placeholder="0" onFocus={e => e.target.select()}
                            onChange={e => handleInputChange(e, { type: "number", maxLength: 3, minLength: 1 })}
                            className="sf-input" style={{ padding: "8px 12px", textAlign: "right" }} />
                        </div>
                        <div>
                          <label className="sf-label">Flat Bonus Rate per Standard Foundation Call</label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--text-muted)" }}>$</span>
                            <input type="text" readOnly name="customerExperience.customerCareCalls.flatBonusRatePerFoundationCall"
                              value={fmtNum(formData.customerExperience.customerCareCalls.flatBonusRatePerFoundationCall) || ""}
                              className="sf-input-ro" style={{ padding: "8px 12px 8px 28px", width: "100%", textAlign: "right" }} />
                          </div>
                        </div>
                        <div>
                          <label className="sf-label">Extensive Foundation Calls</label>
                          <input type="number" name="customerExperience.customerCareCalls.extensiveFoundationCalls"
                            value={formData.customerExperience.customerCareCalls.extensiveFoundationCalls}
                            inputMode="numeric" maxLength={3} minLength={1}
                            disabled={!isEditable} placeholder="0" onFocus={e => e.target.select()}
                            onChange={e => handleInputChange(e, { type: "number", maxLength: 3, minLength: 1 })}
                            className="sf-input" style={{ padding: "8px 12px", textAlign: "right" }} />
                        </div>
                        <div>
                          <label className="sf-label">Flat Bonus Rate per Extensive Foundation Call</label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--text-muted)" }}>$</span>
                            <input type="text" readOnly name="customerExperience.customerCareCalls.flatBonusRatePerExtensiveFoundationCall"
                              value={fmtNum(formData.customerExperience.customerCareCalls.flatBonusRatePerExtensiveFoundationCall) || ""}
                              className="sf-input-ro" style={{ padding: "8px 12px 8px 28px", width: "100%", textAlign: "right" }} />
                          </div>
                        </div>
                        <div>
                          <label className="sf-label">Additional Bonus</label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--text-muted)" }}>$</span>
                            <input type="text" name="customerExperience.customerCareCalls.additionalBonus"
                              value={formData.customerExperience.customerCareCalls.additionalBonus}
                              inputMode="decimal" placeholder="0.00"
                              disabled={!isEditable} onFocus={e => e.target.select()}
                              onChange={e => handleInputChange(e, { type: "decimal", maxLength: 9 })}
                              className="sf-input" style={{ padding: "8px 12px 8px 28px", width: "100%", textAlign: "right" }} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bonus % Earned results strip */}
                  <div className="sf-results" style={{ marginTop: 16 }}>
                    <div className="sf-results-label">Bonus % Earned</div>
                    <input type="number" readOnly name="customerExperience.customerCareCalls.bonusPercentEarned"
                      value={formData.customerExperience.customerCareCalls.bonusPercentEarned}
                      className="sf-input-ro" style={{ width: 96, padding: "5px 8px", textAlign: "right" }} />
                  </div>
                </div>
              </div>

              {/* ── BONUS CALCULATION ──────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
                  {sectionAccent}
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Bonus Calculation</span>
                </div>

                {/* Total Achievement Score label */}
                <div style={{ padding: "8px 16px", background: "var(--bg-surface-raised)", borderBottom: "1px solid var(--border-section)", textAlign: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Total Achievement Score</span>
                </div>

                {/* 3-col top row — responsive */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", background: "var(--bg-surface-raised)", borderBottom: "1px solid var(--border-section)" }}>
                  {[
                    { label: "Flat Bonus Rate per Comp. Call", name: "bonusCalculation.totalAchievementScore.flatBonusRatePerCompletedCall", value: fmtNum(formData.bonusCalculation.totalAchievementScore.flatBonusRatePerCompletedCall), prefix: "$" },
                    { label: "Calls Completed", name: "bonusCalculation.totalAchievementScore.callsCompleted", value: formData.customerExperience.customerCareCalls.totalCallsCompleted, prefix: null },
                    { label: "Bonus Pool Available at 100%", name: "bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100Percent", value: fmtNum(formData.bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100Percent), prefix: "$" },
                  ].map((col, i) => (
                    <div key={i} style={{ padding: "10px 14px", borderLeft: i > 0 ? "1px solid var(--border-section)" : "none" }}>
                      <div className="sf-calc-label">{col.label}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                        {col.prefix && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{col.prefix}</span>}
                        <input type="text" readOnly name={col.name} value={col.value || ""}
                          className="sf-input-ro" style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom row — Bonus Pool / % / Total Payout */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", background: "var(--bg-surface-raised)" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderRight: "1px solid var(--border-section)" }}>
                    {[
                      { label: "Bonus Pool", name: "bonusCalculation.bonusPool", value: fmtNum(formData.bonusCalculation.bonusPool), prefix: "$" },
                      { label: "%", name: "bonusCalculation.percentage", value: formData.bonusCalculation.percentage, prefix: null },
                    ].map((col, i) => (
                      <div key={i} style={{ padding: "10px 12px", textAlign: "center", borderLeft: i > 0 ? "1px solid var(--border-section)" : "none" }}>
                        <div className="sf-calc-label">{col.label}</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          {col.prefix && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{col.prefix}</span>}
                          <input type="text" readOnly name={col.name} value={col.value || ""}
                            className="sf-input-ro" style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: "12px 16px", background: "var(--bg-chip)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Total Bonus Payout</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 15, color: "var(--text-muted)" }}>$</span>
                      <input type="text" readOnly name="bonusCalculation.totalBonusPayout"
                        value={fmtNum(formData.bonusCalculation.totalBonusPayout) || "0"}
                        style={{ background: "transparent", border: "none", outline: "none", fontSize: 18, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", width: 160 }} />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </main>
      </div>

      <BonusApplicationHistory historyItems={historyItems} comments={commentList} historyAccordionExpanded={historyAccordionExpanded} onAccordionToggle={handleHistoryAccordionToggle} />
      <CommentComponent comment={comment} commentList={commentList} commentsAccordionExpanded={commentsAccordionExpanded} onCommentChange={handleCommentChange} onAddComment={handleAddComment} onAccordionToggle={handleCommentsAccordionToggle} />

      <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-section)", background: "var(--bg-surface-raised)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <ActionButtons
          isReviewMode={isReviewMode}
          bonusApplicationId={id && parseInt(id) ? parseInt(id) : bonusDetails?.bonusApplicationId}
          currentStatus={bonusDetails?.currentStatus ?? null}
          bonusSheetJson={getBonusSheetJson()}
          projectNumbers={null}
          amount={parseFloat(formData.bonusCalculation.totalBonusPayout) || 0}
          bonusRecipientEmail={formData.bonusRecipientEmail}
          submitterEmail={formData.submitterEmail}                    
          submitterName={formData.submitterName}
          vpEmail={formData.vpEmail}
          applicationType={formData.applicationType}
          hasOverriddenHierarchy={initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy || false}
          comments={commentList.filter(c => c.commentId <= 0).map(c => c.commentText)}
          formValidationErrors={validateForm()}
          recipientName={formData.managerName}
          recipientJobFamily={recipientJobFamily}
          recipientJobTitle={recipientJobTitle}
          loggedInUserName={loggedInUser.name}
        />
      </div>
    </div>
  );
};

export default CCMForm;